// frontend/src/context/themeContext.tsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'gpcwms_theme';

function getSystemPrefersDark() {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(mode: ThemeMode): 'light' | 'dark' {
    return mode === 'system' ? (getSystemPrefersDark() ? 'dark' : 'light') : mode;
}

function applyResolvedTheme(resolved: 'light' | 'dark') {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
}

interface ThemeContextType {
    theme: ThemeMode;
    resolvedTheme: 'light' | 'dark';
    setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType>({
    theme: 'system',
    resolvedTheme: 'light',
    setTheme: () => { },
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<ThemeMode>(() => {
        const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
        return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
    });
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolve(theme));

    const setTheme = useCallback((mode: ThemeMode) => {
        localStorage.setItem(STORAGE_KEY, mode);
        setThemeState(mode);
    }, []);

    useEffect(() => {
        const resolved = resolve(theme);
        setResolvedTheme(resolved);
        applyResolvedTheme(resolved);

        if (theme !== 'system') return;

        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => {
            const next = resolve('system');
            setResolvedTheme(next);
            applyResolvedTheme(next);
        };
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
